
import { ref, computed, onMounted, watch, nextTick, onUnmounted } from "vue";
import { DownOutlined, UpOutlined } from "@ant-design/icons-vue";
import {
  sysDashboardAccountData,
  sysDashboardUserData,
  sysDashboardUserExpend,
  sysDashboardAmountByUser,
  sysDashboardGmtByUser,
  sysDashboardAccountExpend,
} from "@/api/api";
import * as echarts from "echarts";
import UserSelect from "@/components/UserSelect.vue";

interface AccountData {
  totalAds: number;
  assignedAds: number;
  availableAds: number;
  useInner: number;
  reallyInner: number;
  bannedAds: number;
  bannedCustomer: number;
  bannedTech: number;
  todayNewAds: number;
  yesterdayNewAds: number;
  weekNewAds: number;
  monthNewAds: number;
}

interface UserData {
  totalUsers: number;
  paidUsers: number;
  weekNewUsers: number;
  monthNewUsers: number;
  innerTotalUsers: number;
  innerPaidUsers: number;
  innerWeekNewUsers: number;
  innerMonthNewUsers: number;
  outerTotalUsers: number;
  outerPaidUsers: number;
  outerWeekNewUsers: number;
  outerMonthNewUsers: number;
}

interface ExpendData {
  totalExpend: number;
  totalRecharge: number;
  totalAccountExpend: number;
  totalAdsAccounts: number;
  availableAdsAccounts: number;
  yesterdayExpend: number;
  yesterdayExpendAccountCount: number;
  weekExpend: number;
  monthExpend: number;
  yearExpend: number;
  innerTotalRecharge: number;
  outerTotalRecharge: number;
  innerTotalAdsAccounts: number;
  outerTotalAdsAccounts: number;
  innerTotalExpend: number;
  outerTotalExpend: number;
  innerTotalAccountExpend: number;
  outerTotalAccountExpend: number;
  innerAvailableAdsAccounts: number;
  outerAvailableAdsAccounts: number;
  innerYesterdayExpend: number;
  outerYesterdayExpend: number;
  innerYesterdayExpendAccountCount: number;
  outerYesterdayExpendAccountCount: number;
  innerWeekExpend: number;
  outerWeekExpend: number;
  innerMonthExpend: number;
  outerMonthExpend: number;
  innerYearExpend: number;
  outerYearExpend: number;
}

interface AmountByUserData {
  totalExpend: number;
  yesterday: number;
  beforeYesterday: number;
  thisMonth: number;
  lastMonth: number;
  dayGrowthPercent: number | null;
  monthGrowthPercent: number | null;
}

/** 账户消耗数据 /sysDashboard/accountExpend（接口返回 expendCount, monthStartCount, yesterdayStartCount；封禁数为 0 占位） */
interface AccountExpendData {
  /** 有消耗记录总账户数 */
  expendCount: number;
  /** 昨日启动消耗账户数 */
  yesterdayStartCount: number;
  /** 本月启动消耗账户数 */
  monthStartCount: number;
  /** 昨日封禁账户数（暂无接口，显示 0） */
  yesterdayBannedCount: number;
  /** 本月封禁账户数（暂无接口，显示 0） */
  monthBannedCount: number;
}

/** 时区分类数据项 */
interface GmtByUserItem {
  gmt: string;
  totalCount: number;
  banCount: number;
  usingCount: number;
  availableCount: number;
  monthCreateCount: number;
  yesterdayCreateCount: number;
  totalExpend: number;
}

const GMT_DEFAULT_ROWS = 2;
const GMT_COLS = 4;
// 默认 2 行 4 列，首格为「总时区数」，故时区卡片显示 4×2-1=7 张
const GMT_DEFAULT_COUNT = GMT_DEFAULT_ROWS * GMT_COLS - 1;

export default {
  name: "HomePage",
  components: { DownOutlined, UpOutlined, UserSelect },
  setup() {
    const accountData = ref<AccountData>({
      totalAds: 0,
      assignedAds: 0,
      availableAds: 0,
      useInner: 0,
      reallyInner: 0,
      bannedAds: 0,
      bannedCustomer: 0,
      bannedTech: 0,
      todayNewAds: 0,
      yesterdayNewAds: 0,
      weekNewAds: 0,
      monthNewAds: 0,
    });

    const userData = ref<UserData>({
      totalUsers: 0,
      paidUsers: 0,
      weekNewUsers: 0,
      monthNewUsers: 0,
      innerTotalUsers: 0,
      innerPaidUsers: 0,
      innerWeekNewUsers: 0,
      innerMonthNewUsers: 0,
      outerTotalUsers: 0,
      outerPaidUsers: 0,
      outerWeekNewUsers: 0,
      outerMonthNewUsers: 0,
    });

    const expendData = ref<ExpendData>({
      totalExpend: 0,
      totalRecharge: 0,
      totalAccountExpend: 0,
      totalAdsAccounts: 0,
      availableAdsAccounts: 0,
      yesterdayExpend: 0,
      yesterdayExpendAccountCount: 0,
      weekExpend: 0,
      monthExpend: 0,
      yearExpend: 0,
      innerTotalRecharge: 0,
      outerTotalRecharge: 0,
      innerTotalAdsAccounts: 0,
      outerTotalAdsAccounts: 0,
      innerTotalExpend: 0,
      outerTotalExpend: 0,
      innerTotalAccountExpend: 0,
      outerTotalAccountExpend: 0,
      innerAvailableAdsAccounts: 0,
      outerAvailableAdsAccounts: 0,
      innerYesterdayExpend: 0,
      outerYesterdayExpend: 0,
      innerYesterdayExpendAccountCount: 0,
      outerYesterdayExpendAccountCount: 0,
      innerWeekExpend: 0,
      outerWeekExpend: 0,
      innerMonthExpend: 0,
      outerMonthExpend: 0,
      innerYearExpend: 0,
      outerYearExpend: 0,
    });

    const amountByUserData = ref<AmountByUserData>({
      totalExpend: 0,
      yesterday: 0,
      beforeYesterday: 0,
      thisMonth: 0,
      lastMonth: 0,
      dayGrowthPercent: null,
      monthGrowthPercent: null,
    });

    const accountExpendData = ref<AccountExpendData>({
      expendCount: 0,
      yesterdayStartCount: 0,
      monthStartCount: 0,
      yesterdayBannedCount: 0,
      monthBannedCount: 0,
    });

    const gmtByUserData = ref<GmtByUserItem[]>([]);
    const gmtExpanded = ref(false);

    // 按客户查看：选择客户后的消耗与账户状态（先占位，待接口字段确认后对接）
    const customerUserId = ref<number | string | null>(""); // 默认「全部」，全部时不传 userId
    const customerStats = ref({
      totalRecharge: 0,
      totalExpend: 0,
      monthExpend: 0,
      monthGrowthPercent: null as number | null,
      yesterdayExpend: 0,
      dayGrowthPercent: null as number | null,
    });

    const gmtDisplayList = computed(() =>
      gmtExpanded.value ? gmtByUserData.value : gmtByUserData.value.slice(0, GMT_DEFAULT_COUNT)
    );
    const gmtHasMore = computed(() => gmtByUserData.value.length > GMT_DEFAULT_COUNT);

    const loading = ref(false);
    const expendChartRef = ref<HTMLElement | null>(null);
    const bannedChartRef = ref<HTMLElement | null>(null);
    const accountChartRef = ref<HTMLElement | null>(null);
    const gmtPieChartRef = ref<HTMLElement | null>(null);
    let expendChart: echarts.ECharts | null = null;
    let bannedChart: echarts.ECharts | null = null;
    let accountChart: echarts.ECharts | null = null;
    let gmtPieChart: echarts.ECharts | null = null;

    // 动画数字的响应式数据
    const animatedAccountData = ref<AccountData>({
      totalAds: 0,
      assignedAds: 0,
      availableAds: 0,
      useInner: 0,
      reallyInner: 0,
      bannedAds: 0,
      bannedCustomer: 0,
      bannedTech: 0,
      todayNewAds: 0,
      yesterdayNewAds: 0,
      weekNewAds: 0,
      monthNewAds: 0,
    });

    const animatedUserData = ref<UserData>({
      totalUsers: 0,
      paidUsers: 0,
      weekNewUsers: 0,
      monthNewUsers: 0,
      innerTotalUsers: 0,
      innerPaidUsers: 0,
      innerWeekNewUsers: 0,
      innerMonthNewUsers: 0,
      outerTotalUsers: 0,
      outerPaidUsers: 0,
      outerWeekNewUsers: 0,
      outerMonthNewUsers: 0,
    });

    const animatedExpendData = ref<ExpendData>({
      totalExpend: 0,
      totalRecharge: 0,
      totalAccountExpend: 0,
      totalAdsAccounts: 0,
      availableAdsAccounts: 0,
      yesterdayExpend: 0,
      yesterdayExpendAccountCount: 0,
      weekExpend: 0,
      monthExpend: 0,
      yearExpend: 0,
      innerTotalRecharge: 0,
      outerTotalRecharge: 0,
      innerTotalAdsAccounts: 0,
      outerTotalAdsAccounts: 0,
      innerTotalExpend: 0,
      outerTotalExpend: 0,
      innerTotalAccountExpend: 0,
      outerTotalAccountExpend: 0,
      innerAvailableAdsAccounts: 0,
      outerAvailableAdsAccounts: 0,
      innerYesterdayExpend: 0,
      outerYesterdayExpend: 0,
      innerYesterdayExpendAccountCount: 0,
      outerYesterdayExpendAccountCount: 0,
      innerWeekExpend: 0,
      outerWeekExpend: 0,
      innerMonthExpend: 0,
      outerMonthExpend: 0,
      innerYearExpend: 0,
      outerYearExpend: 0,
    });

    // 数字动画函数
    const animateNumber = (
      start: number,
      end: number,
      duration: number,
      callback: (value: number) => void
    ) => {
      const startTime = Date.now();
      const difference = end - start;

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // 使用缓动函数（easeOutCubic）
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const current = Math.floor(start + difference * easeOutCubic);

        callback(current);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          callback(end);
        }
      };

      animate();
    };

    // 启动所有数字动画
    const startAnimations = () => {
      // 广告账户数据动画
      Object.keys(accountData.value).forEach((key) => {
        const targetValue = accountData.value[key as keyof AccountData];
        animateNumber(
          animatedAccountData.value[key as keyof AccountData],
          targetValue,
          1500,
          (value) => {
            (animatedAccountData.value as any)[key] = value;
          }
        );
      });

      // 用户数据动画
      Object.keys(userData.value).forEach((key) => {
        const targetValue = userData.value[key as keyof UserData];
        animateNumber(
          animatedUserData.value[key as keyof UserData],
          targetValue,
          1500,
          (value) => {
            (animatedUserData.value as any)[key] = value;
          }
        );
      });

      // 消耗数据动画
      Object.keys(expendData.value).forEach((key) => {
        const targetValue = expendData.value[key as keyof ExpendData];
        animateNumber(
          animatedExpendData.value[key as keyof ExpendData],
          targetValue,
          1500,
          (value) => {
            (animatedExpendData.value as any)[key] = value;
          }
        );
      });
    };

    // 格式化金额
    const formatMoney = (value: number | undefined) => {
      if (!value) return "0.00";
      return Number(value).toLocaleString("zh-CN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    // 首页统计数字：仅当内容宽度超过容器时，自动缩小字号以避免溢出（不换行）
    const fitStatValues = () => {
      const elements = Array.from(
        document.querySelectorAll<HTMLElement>(".stat-card .stat-value")
      );
      if (!elements.length) return;

      elements.forEach((el) => {
        const parent = el.parentElement as HTMLElement | null;
        if (!parent) return;

        const maxWidth = el.clientWidth;
        if (!maxWidth) return;

        const maxFontSize = 24;
        const minFontSize = 10;

        el.style.whiteSpace = "nowrap";

        // 初始设为最大字号，然后逐步减小到不溢出（或到最小字号为止）
        let currentFontSize = maxFontSize;
        el.style.fontSize = `${currentFontSize}px`;

        // scrollWidth > clientWidth 表示实际内容宽度超出容器
        let guard = 0;
        while (
          guard < 30 &&
          el.scrollWidth > maxWidth + 1 &&
          currentFontSize > minFontSize
        ) {
          currentFontSize = Math.max(minFontSize, currentFontSize - 1);
          el.style.fontSize = `${currentFontSize}px`;
          guard++;
        }
      });
    };

    // 动画前：先把 animated* 临时填到最终目标值，确保测量时数字是“最终版”
    const setAnimatedValuesToTargets = () => {
      Object.keys(accountData.value).forEach((key) => {
        (animatedAccountData.value as any)[key] =
          accountData.value[key as keyof AccountData] as number;
      });

      Object.keys(userData.value).forEach((key) => {
        (animatedUserData.value as any)[key] =
          userData.value[key as keyof UserData] as number;
      });

      Object.keys(expendData.value).forEach((key) => {
        (animatedExpendData.value as any)[key] =
          expendData.value[key as keyof ExpendData] as number;
      });
    };

    // 动画前测量后：重置回 0，让动画仍然从 0 开始
    const resetAnimatedValuesToZero = () => {
      Object.keys(animatedAccountData.value).forEach((key) => {
        (animatedAccountData.value as any)[key] = 0;
      });
      Object.keys(animatedUserData.value).forEach((key) => {
        (animatedUserData.value as any)[key] = 0;
      });
      Object.keys(animatedExpendData.value).forEach((key) => {
        (animatedExpendData.value as any)[key] = 0;
      });
    };

    // 初始化消耗数据柱状图
    const initExpendChart = () => {
      if (!expendChartRef.value) return;

      expendChart = echarts.init(expendChartRef.value);

      const updateChart = () => {
        if (!expendChart) return;

        const categories = [
          "总计消耗",
          "本年消耗",
          "当月消耗",
          "本周消耗",
          "昨日消耗",
        ];
        const innerValues = [
          expendData.value.innerTotalExpend || 0,
          expendData.value.innerYearExpend || 0,
          expendData.value.innerMonthExpend || 0,
          expendData.value.innerWeekExpend || 0,
          expendData.value.innerYesterdayExpend || 0,
        ];
        const outerValues = [
          expendData.value.outerTotalExpend || 0,
          expendData.value.outerYearExpend || 0,
          expendData.value.outerMonthExpend || 0,
          expendData.value.outerWeekExpend || 0,
          expendData.value.outerYesterdayExpend || 0,
        ];
        const totalValues = [
          expendData.value.totalExpend || 0,
          expendData.value.yearExpend || 0,
          expendData.value.monthExpend || 0,
          expendData.value.weekExpend || 0,
          expendData.value.yesterdayExpend || 0,
        ];

        const option = {
          tooltip: {
            trigger: "axis",
            axisPointer: {
              type: "shadow",
            },
            formatter: (params: any) => {
              let result = params[0].name + "<br/>";
              params.forEach((item: any) => {
                result += `${item.marker}${item.seriesName}: $${formatMoney(item.value)}<br/>`;
              });
              return result;
            },
          },
          legend: {
            data: ["内部消耗", "外部消耗", "总消耗"],
            top: 10,
          },
          grid: {
            left: "3%",
            right: "4%",
            bottom: "3%",
            containLabel: true,
          },
          xAxis: {
            type: "category",
            data: categories,
            axisTick: {
              alignWithLabel: true,
            },
            axisLabel: {
              rotate: 0,
              fontSize: 12,
            },
          },
          yAxis: {
            type: "value",
            axisLabel: {
              formatter: (value: number) => {
                if (value >= 10000) {
                  return "$" + (value / 10000).toFixed(1) + "万";
                }
                return "$" + value.toFixed(0);
              },
            },
          },
          series: [
            {
              name: "内部消耗",
              type: "bar",
              barGap: 0,
              data: innerValues,
              itemStyle: {
                color: "#1890ff",
              },
              label: {
                show: true,
                position: "top",
                formatter: (params: any) => {
                  return "$" + formatMoney(params.value);
                },
                fontSize: 11,
              },
            },
            {
              name: "外部消耗",
              type: "bar",
              barGap: 0,
              data: outerValues,
              itemStyle: {
                color: "#52c41a",
              },
              label: {
                show: true,
                position: "top",
                formatter: (params: any) => {
                  return "$" + formatMoney(params.value);
                },
                fontSize: 11,
              },
            },
            {
              name: "总消耗",
              type: "bar",
              barGap: 0,
              data: totalValues,
              itemStyle: {
                color: "#faad14",
              },
              label: {
                show: true,
                position: "top",
                formatter: (params: any) => {
                  return "$" + formatMoney(params.value);
                },
                fontSize: 11,
              },
            },
          ],
        };

        expendChart.setOption(option);
      };

      updateChart();

      // 监听数据变化，更新图表
      watch(
        () => animatedExpendData.value,
        () => {
          nextTick(() => {
            updateChart();
          });
        },
        { deep: true }
      );
    };

    // 初始化封禁数据饼图
    const initBannedChart = () => {
      if (!bannedChartRef.value) return;

      bannedChart = echarts.init(bannedChartRef.value);

      const updateChart = () => {
        if (!bannedChart) return;

        const totalBanned = accountData.value.bannedAds || 0;
        const customerBanned = accountData.value.bannedCustomer || 0;
        const techBanned = accountData.value.bannedTech || 0;
        const unknownBanned = totalBanned - customerBanned - techBanned;

        const option = {
          tooltip: {
            trigger: "item",
            formatter: (params: {
              name: string;
              value: number;
              percent: number;
            }) => {
              return `${params.name}<br/>数量: ${params.value}<br/>占比: ${params.percent}%`;
            },
          },
          legend: { show: false },
          series: [
            {
              name: "封禁数据",
              type: "pie",
              radius: ["40%", "70%"],
              center: ["50%", "50%"],
              avoidLabelOverlap: false,
              itemStyle: {
                borderRadius: 10,
                borderColor: "#fff",
                borderWidth: 2,
              },
              label: {
                show: true,
                formatter: (params: {
                  name: string;
                  value: number;
                  percent: number;
                }) => {
                  return `${params.name}\n${params.value} (${params.percent}%)`;
                },
                fontSize: 12,
              },
              emphasis: {
                label: {
                  show: true,
                  fontSize: 14,
                  fontWeight: "bold",
                },
              },
              labelLine: {
                show: true,
              },
              data: [
                {
                  value: customerBanned,
                  name: "客户原因封禁",
                  itemStyle: {
                    color: "#faad14",
                  },
                },
                {
                  value: techBanned,
                  name: "技术封禁",
                  itemStyle: {
                    color: "#ff4d4f",
                  },
                },
                {
                  value: unknownBanned > 0 ? unknownBanned : 0,
                  name: "未知",
                  itemStyle: {
                    color: "#d9d9d9",
                  },
                },
              ].filter((item) => item.value > 0),
            },
          ],
        };

        bannedChart.setOption(option);
      };

      updateChart();

      // 监听数据变化，更新图表
      watch(
        () => accountData.value,
        () => {
          nextTick(() => {
            updateChart();
          });
        },
        { deep: true }
      );
    };

    // 初始化账户分配饼图
    const initAccountChart = () => {
      if (!accountChartRef.value) return;

      accountChart = echarts.init(accountChartRef.value);

      const updateChart = () => {
        if (!accountChart) return;

        const assignedAds = accountData.value.assignedAds || 0;
        const availableAds = accountData.value.availableAds || 0;
        const useInner = accountData.value.useInner || 0;
        const reallyInner = accountData.value.reallyInner || 0;
        const bannedAds = accountData.value.bannedAds || 0;

        const option = {
          tooltip: {
            trigger: "item",
            formatter: (params: {
              name: string;
              value: number;
              percent: number;
            }) => {
              return `${params.name}<br/>数量: ${params.value}<br/>占比: ${params.percent}%`;
            },
          },
          legend: { show: false },
          series: [
            {
              name: "账户分配",
              type: "pie",
              radius: ["40%", "70%"],
              center: ["50%", "50%"],
              avoidLabelOverlap: false,
              itemStyle: {
                borderRadius: 10,
                borderColor: "#fff",
                borderWidth: 2,
              },
              label: {
                show: true,
                formatter: (params: {
                  name: string;
                  value: number;
                  percent: number;
                }) => {
                  return `${params.name}\n${params.value} (${params.percent}%)`;
                },
                fontSize: 12,
              },
              emphasis: {
                label: {
                  show: true,
                  fontSize: 14,
                  fontWeight: "bold",
                },
              },
              labelLine: {
                show: true,
              },
              data: [
                {
                  value: availableAds,
                  name: "可用(未分配)",
                  itemStyle: { color: "#52c41a" },
                },
                {
                  value: assignedAds,
                  name: "外部-已分配",
                  itemStyle: { color: "#1890ff" },
                },
                {
                  value: useInner,
                  name: "内部-使用中",
                  itemStyle: { color: "#722ed1" },
                },
                {
                  value: reallyInner,
                  name: "内部-养户中",
                  itemStyle: { color: "#fa8c16" },
                },
                {
                  value: bannedAds,
                  name: "封禁总数",
                  itemStyle: { color: "#ff4d4f" },
                },
              ].filter((item) => item.value > 0),
            },
          ],
        };

        accountChart.setOption(option);
      };

      updateChart();

      // 监听数据变化，更新图表
      watch(
        () => accountData.value,
        () => {
          nextTick(() => {
            updateChart();
          });
        },
        { deep: true }
      );
    };

    const initGmtPieChart = () => {
      if (!gmtPieChartRef.value) return;
      gmtPieChart = echarts.init(gmtPieChartRef.value);

      const updateChart = () => {
        if (!gmtPieChart) return;

        const data = gmtByUserData.value
          .filter((i) => i.usingCount > 0)
          .map((i) => ({ value: i.usingCount, name: i.gmt || "空时区" }));

        const option = {
          tooltip: {
            trigger: "item",
            formatter: (params: { name: string; value: number; percent: number }) =>
              `${params.name}<br/>在用数量: ${params.value}<br/>占比: ${params.percent}%`,
          },
          legend: { show: false },
          series: [
            {
              name: "时区在用账户",
              type: "pie",
              radius: ["40%", "70%"],
              center: ["50%", "50%"],
              avoidLabelOverlap: true,
              itemStyle: { borderRadius: 10, borderColor: "#fff", borderWidth: 2 },
              label: {
                show: true,
                formatter: (params: { name: string; value: number; percent: number }) =>
                  `${params.name}\n${params.value} (${params.percent}%)`,
                fontSize: 11,
              },
              emphasis: { label: { show: true, fontSize: 13, fontWeight: "bold" } },
              labelLine: { show: true },
              data,
            },
          ],
        };
        gmtPieChart.setOption(option);
      };

      updateChart();
      watch(() => gmtByUserData.value, () => nextTick(updateChart), { deep: true });
    };

    // 获取广告账户数据
    const getAccountData = async () => {
      try {
        loading.value = true;
        const res = await sysDashboardAccountData({});
        if (res.code === "200" && res.data) {
          accountData.value = res.data;
        }
      } catch (error) {
        console.error("获取广告账户数据失败:", error);
      } finally {
        loading.value = false;
      }
    };

    // 获取客户数量数据
    const getUserData = async () => {
      try {
        loading.value = true;
        const res = await sysDashboardUserData({});
        if (res.code === "200" && res.data) {
          userData.value = res.data;
        }
      } catch (error) {
        console.error("获取客户数量数据失败:", error);
      } finally {
        loading.value = false;
      }
    };

    // 获取消耗数据
    const getExpendData = async () => {
      try {
        loading.value = true;
        const res = await sysDashboardUserExpend({});
        if (res.code === "200" && res.data) {
          expendData.value = res.data;
        }
      } catch (error) {
        console.error("获取消耗数据失败:", error);
      } finally {
        loading.value = false;
      }
    };

    // 获取用户消耗数据（beforeYesterday=前天，lastMonth=上月，用于计算环比）
    const getAmountByUser = async () => {
      try {
        const res = await sysDashboardAmountByUser({});
        if (res.code === "200" && res.data) {
          const thisMonth = Number(res.data.thisMonth) || 0;
          const lastMonth = Number(res.data.lastMonth) || 0;
          const yesterday = Number(res.data.yesterday) || 0;
          const beforeYesterday = Number(res.data.beforeYesterday ?? res.data.before_yesterday) || 0;
          const monthGrowthPercent =
            lastMonth !== 0 ? Math.round(((thisMonth - lastMonth) / lastMonth) * 10000) / 100 : null;
          const dayGrowthPercent =
            beforeYesterday !== 0 ? Math.round(((yesterday - beforeYesterday) / beforeYesterday) * 10000) / 100 : null;
          amountByUserData.value = {
            totalExpend: res.data.totalExpend ?? 0,
            yesterday,
            beforeYesterday,
            thisMonth,
            lastMonth,
            dayGrowthPercent,
            monthGrowthPercent,
          };
        }
      } catch (error) {
        console.error("获取用户消耗数据失败:", error);
      }
    };

    // 获取账户消耗数据 /sysDashboard/accountExpend
    const getAccountExpend = async () => {
      try {
        const res = await sysDashboardAccountExpend({});
        if (res.code === "200" && res.data) {
          const d = res.data;
          accountExpendData.value = {
            expendCount: Number(d.expendCount ?? d.expend_count) || 0,
            yesterdayStartCount: Number(d.yesterdayStartCount ?? d.yesterday_start_count) || 0,
            monthStartCount: Number(d.monthStartCount ?? d.month_start_count) || 0,
            yesterdayBannedCount: Number(d.yesterdayBannedCount ?? d.yesterday_banned_count) || 0,
            monthBannedCount: Number(d.monthBannedCount ?? d.month_banned_count) || 0,
          };
        }
      } catch (error) {
        console.error("获取账户消耗数据失败:", error);
      }
    };

    // 获取时区分类数据
    const getGmtByUser = async () => {
      try {
        const res = await sysDashboardGmtByUser({});
        if (res.code === "200" && Array.isArray(res.data)) {
          gmtByUserData.value = res.data.map((item: GmtByUserItem) => ({
            gmt: item.gmt ?? "",
            totalCount: item.totalCount ?? 0,
            banCount: item.banCount ?? 0,
            usingCount: item.usingCount ?? 0,
            availableCount: item.availableCount ?? 0,
            monthCreateCount: item.monthCreateCount ?? 0,
            yesterdayCreateCount: item.yesterdayCreateCount ?? 0,
            totalExpend: item.totalExpend ?? 0,
          }));
        }
      } catch (error) {
        console.error("获取时区分类数据失败:", error);
      }
    };

    // 时区总账户数（所有时区 totalCount 之和）
    const gmtTotalCount = computed(() =>
      gmtByUserData.value.reduce((sum, item) => sum + (item.totalCount || 0), 0)
    );

    // 格式化时区显示：GMT+5 -> 东五区 UTC/GMT+05:00
    const formatGmtLabel = (gmt: string) => {
      if (!gmt || gmt === "") return "未区分时区";
      const match = gmt.match(/GMT([+-])(\d+)/i);
      if (!match) return gmt;
      const sign = match[1];
      const num = parseInt(match[2], 10);
      const dir = sign === "+" ? "东" : "西";
      const pad = String(num).padStart(2, "0");
      return `${dir}${num}区 UTC/GMT${sign}${pad}:00`;
    };

    // 封户率、单户平均消耗
    const getBanRate = (item: GmtByUserItem) =>
      item.totalCount > 0 ? Math.round((item.banCount / item.totalCount) * 100) : 0;
    const getGmtAvgExpend = (item: GmtByUserItem) =>
      item.totalCount > 0 ? item.totalExpend / item.totalCount : 0;

    // 按客户查看：选择客户后拉取该用户的消耗与账户状态
    const resetCustomerStats = () => {
      customerStats.value = {
        totalRecharge: 0,
        totalExpend: 0,
        monthExpend: 0,
        monthGrowthPercent: null,
        yesterdayExpend: 0,
        dayGrowthPercent: null,
      };
    };

    const onCustomerUserChange = async () => {
      const uid = customerUserId.value;
      const isAll = uid === null || uid === undefined || uid === "";
      try {
        const amountRes = await sysDashboardAmountByUser(isAll ? {} : { userId: uid });
        if (amountRes.code === "200" && amountRes.data) {
          const d = amountRes.data;
          const thisMonth = Number(d.thisMonth) || 0;
          const lastMonth = Number(d.lastMonth) || 0;
          const yesterday = Number(d.yesterday) || 0;
          const beforeYesterday = Number(d.beforeYesterday ?? d.before_yesterday) || 0;
          customerStats.value.totalExpend = Number(d.totalExpend) || 0;
          customerStats.value.yesterdayExpend = yesterday;
          customerStats.value.monthExpend = thisMonth;
          customerStats.value.totalRecharge = Number(d.totalRecharge) || 0;
          customerStats.value.monthGrowthPercent =
            lastMonth !== 0 ? Math.round(((thisMonth - lastMonth) / lastMonth) * 10000) / 100 : null;
          customerStats.value.dayGrowthPercent =
            beforeYesterday !== 0 ? Math.round(((yesterday - beforeYesterday) / beforeYesterday) * 10000) / 100 : null;
        }
      } catch (e) {
        console.error("按客户查看数据失败:", e);
        resetCustomerStats();
      }
    };

    // 加载所有数据
    const loadAllData = async () => {
      await Promise.all([getAccountData(), getUserData(), getExpendData()]);
      await getAmountByUser();
      await getAccountExpend();
      await getGmtByUser();
      await onCustomerUserChange();
      // 所有数据加载完成后：动画前先测量一次字体适配（不换行）
      setAnimatedValuesToTargets();
      await nextTick();
      fitStatValues();
      // 重置回 0，保证动画效果正常
      resetAnimatedValuesToZero();
      startAnimations();
    };

    onMounted(() => {
      loadAllData();

      // 窗口缩放时重新适配
      let resizeTimer: number | undefined;
      const onResize = () => {
        if (resizeTimer) window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
          fitStatValues();
        }, 200);
      };
      window.addEventListener("resize", onResize);

      nextTick(() => {
        initExpendChart();
        initBannedChart();
        initAccountChart();
        initGmtPieChart();
      });

      onUnmounted(() => {
        window.removeEventListener("resize", onResize);
        if (resizeTimer) window.clearTimeout(resizeTimer);
      });
    });

    return {
      accountData,
      userData,
      expendData,
      amountByUserData,
      accountExpendData,
      gmtByUserData,
      gmtExpanded,
      gmtDisplayList,
      gmtHasMore,
      gmtTotalCount,
      formatGmtLabel,
      getBanRate,
      getGmtAvgExpend,
      customerUserId,
      customerStats,
      onCustomerUserChange,
      animatedAccountData,
      animatedUserData,
      animatedExpendData,
      loading,
      formatMoney,
      expendChartRef,
      bannedChartRef,
      accountChartRef,
      gmtPieChartRef,
    };
  },
};
